import { useCallback, useEffect, useState } from 'react';
import { fetchAddress, saveAddress } from './api';
import type { AddressPayload, AddressUpdateResponse, AddressValues } from './types';

export function useAddressForm(endpoint: string, nonce: string, type: 'billing' | 'shipping', open: boolean) {
  const [payload, setPayload] = useState<AddressPayload | null>(null);
  const [values, setValues] = useState<AddressValues>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAddress({ endpoint, nonce, type })
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        setValues(data.values || {});
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || 'Fehler beim Laden');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [endpoint, nonce, type, open]);

  const onChange = useCallback((key: string, val: string) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  }, []);

  const onSubmit = useCallback(async (): Promise<AddressUpdateResponse | null> => {
    if (!payload) return null;
    setSaving(true);
    setError(null);
    try {
      return await saveAddress({ endpoint, nonce, type, values });
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Speichern');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [endpoint, nonce, type, values, payload]);

  return { payload, values, loading, saving, error, onChange, onSubmit };
}
